// ─────────────────────────────────────────────────────────────────────────
// Client-side auth: one place that answers "who is using the editor?".
//
// Netlify Identity is the only account system. Two states:
//   identity  Identity is enabled → open self-registration, password resets
//             and as many users as needed, all handled by the widget. API
//             calls carry the user's JWT; local storage is scoped by the
//             stable Identity user id.
//   open      Identity is not enabled. Storage is unscoped and the cloud API
//             refuses every call, so decks stay device-local. The edge
//             function (netlify/edge-functions/editor-auth.js) normally stops
//             a deploy from reaching this state at all.
//
// The EDITOR_USERS / EDITOR_PASSWORD cookie gate that used to sit alongside
// Identity is gone — see that edge function for what replaced it.
//
// initAuth() must resolve before any studio module is imported (model.js
// reads storageScope() at module-init time); studio-entry.jsx guarantees
// this by importing the app dynamically.
// ─────────────────────────────────────────────────────────────────────────
import netlifyIdentity from "netlify-identity-widget";

let mode = "open";        // "identity" | "open"
let identityUser = null;  // netlify-identity-widget user object

// ── Email-link flows ─────────────────────────────────────────────────────
// A page opened from an Identity email carries its token in the URL hash:
// #invite_token=… when someone accepts an invitation, #recovery_token=… when
// they reset a password. The widget reads that hash exactly once, at init,
// keeps the token only in memory, and then erases the hash — so from the
// moment the page loads, a reload throws the token away for good. The
// invitee lands back on the sign-in gate instead of the password form, and
// submitting later posts no token at all, which GoTrue answers with
// "User not found".
//
// The reload that does this is ours, and it is not a deliberate sign-out.
// The widget fires "logout" on any change of its user from set to unset —
// including the background check in its own store.init(), which loads a
// session from localStorage and clears it when the server will not validate
// it. A browser holding a stale session is exactly the state someone is in
// when they accept an invitation on a machine that was signed in before.
//
// So: read the hash before init() erases it, and hold the reload off until
// the flow has finished one way or the other.
const EMAIL_TOKEN = /(confirmation|invite|recovery|email_change)_token=/;
let emailFlow = false;

// Detect Identity by probing GoTrue's public settings endpoint — it only
// exists (as JSON) when Identity is enabled for the site.
async function identityEnabled() {
  try {
    const r = await fetch("/.netlify/identity/settings", { headers: { accept: "application/json" } });
    return r.ok && (r.headers.get("content-type") || "").includes("json");
  } catch { return false; }
}

// → { mode, user } — user is null in identity mode until someone signs in
// (studio-entry.jsx shows the sign-in gate in that case).
export async function initAuth() {
  if (await identityEnabled()) {
    mode = "identity";
    emailFlow = EMAIL_TOKEN.test(window.location.hash || "");
    // init() must stay AHEAD of the handlers below, and this is load-bearing
    // rather than tidy: it calls the widget's store.init() synchronously,
    // which reads any stored session and so fires "login" before we are
    // listening. Registering first would reload every page load for an
    // already-signed-in user, and that loop has no exit.
    netlifyIdentity.init();
    // Reloading after login/logout re-derives the storage scope and cloud
    // session from scratch — much simpler than hot-swapping libraries.
    netlifyIdentity.on("login", () => { netlifyIdentity.close(); window.location.reload(); });
    netlifyIdentity.on("logout", () => { if (!emailFlow) window.location.reload(); });
    // Closing the widget ends the email flow: whatever the token was for is
    // either done — in which case "login" has already reloaded us — or
    // abandoned, and a later sign-out should reload as it always did.
    netlifyIdentity.on("close", () => { emailFlow = false; });
    // The widget renders its errors inside its own iframe, where nothing
    // else can see them and no reload survives them. A failed invitation is
    // the one worth a trace in the console: GoTrue answers 404 "User not
    // found" when the token matches no account, which means the link was
    // superseded by a later invitation, has been used once already, or the
    // account it was issued for is gone.
    // The widget clears its error at the start of every action, so this
    // fires with nothing to report as often as it fires with something.
    netlifyIdentity.on("error", (err) => { if (err) console.error("Netlify Identity:", err); });
    identityUser = netlifyIdentity.currentUser();
    return { mode, user: identityUser };
  }
  mode = "open";
  return { mode, user: null };
}

// Opens the Identity widget (sign in / sign up / recover password).
export function openLogin() { netlifyIdentity.open(); }

// Namespace for localStorage keys. The "id-" prefix is kept from when cookie
// usernames shared this space: it is part of existing users' storage keys, so
// dropping it would orphan their local decks.
export function storageScope() {
  return mode === "identity" && identityUser ? "id-" + identityUser.id : "";
}

// Short human-readable name for the signed-in user ("" when anonymous).
export function userLabel() {
  if (mode === "identity" && identityUser) return identityUser.user_metadata?.full_name || identityUser.email || "signed in";
  return "";
}

export function canSignOut() { return mode === "identity" && !!identityUser; }

export function doSignOut() {
  if (mode !== "identity") return;
  emailFlow = false;   // asked for by hand, so it always reloads
  netlifyIdentity.logout();
}

// Extra headers for the site's own API calls: user.jwt() returns a fresh
// access token, auto-refreshing via the stored refresh token.
//
// The refresh is a network call to GoTrue, and it can fail for a moment on a
// phone that has just changed cell. Giving up at the first failure sends the
// request with no Authorization at all, and every endpoint answers that with
// "sign in to the editor" — which is both wrong (you are signed in) and
// unactionable (signing in again changes nothing). So a failure is tried once
// more before the header is dropped.
export async function authHeader() {
  const u = mode === "identity" ? netlifyIdentity.currentUser() : null;
  if (!u) return {};
  for (let attempt = 0; attempt < 2; attempt++) {
    try { return { Authorization: "Bearer " + (await u.jwt()) }; }
    catch { if (attempt === 0) await new Promise((r) => setTimeout(r, 500)); }
  }
  return {};
}
